import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { getAnalyticsApiBaseUrl } from '../../../config/api';
import { fetchWithAuth } from '../../../utils/auth';
import { PeriodConfig, formatNumber } from '../types';

type Channel = 'all' | 'facebook' | 'google_ads' | 'tiktok';
type SortKey = 'spend' | 'roi' | 'paid' | 'revenue' | 'cpaPaid' | 'installs' | 'campaign';

interface CampaignRow {
  channel: 'facebook' | 'google_ads' | 'tiktok';
  accountId?: string;
  accountName?: string;
  campaignId: string;
  campaignName: string;
  spend: number;
  impressions: number;
  clicks: number;
  installs: number;
  trials: number;
  paid: number;
  revenue: number;
  ctr: number;
  cpi: number;
  cpaPaid: number;
  roas: number;
  roi: number;
}

interface ApiResponse {
  from: string;
  to: string;
  channel: string;
  count: number;
  totals: {
    spend: number;
    impressions: number;
    clicks: number;
    installs: number;
    trials: number;
    paid: number;
    revenue: number;
    roi: number;
  };
  rows: CampaignRow[];
}

const CHANNEL_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  google_ads: 'Google',
  tiktok: 'TikTok',
};

const CHANNEL_COLOR: Record<string, string> = {
  facebook: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
  google_ads: 'bg-green-500/20 text-green-300 border border-green-500/30',
  tiktok: 'bg-pink-500/20 text-pink-300 border border-pink-500/30',
};

function getVerdict(r: CampaignRow): { text: string; color: string } {
  if (r.spend < 5) return { text: '—', color: 'text-gray-500' };
  if (r.paid === 0 && r.spend >= 20) return { text: 'KILL', color: 'text-red-500 font-bold' };
  if (r.roi >= 200) return { text: 'SCALE 2×', color: 'text-emerald-400 font-bold' };
  if (r.roi >= 50) return { text: 'SCALE', color: 'text-emerald-400' };
  if (r.roi >= 0) return { text: 'KEEP', color: 'text-green-400' };
  if (r.roi >= -50) return { text: 'WATCH', color: 'text-yellow-400' };
  if (r.roi >= -90) return { text: 'REDUCE', color: 'text-orange-400' };
  return { text: 'KILL', color: 'text-red-400' };
}

const AdsRoiTab: React.FC<{ period: PeriodConfig }> = ({ period }) => {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channel, setChannel] = useState<Channel>('all');
  const [sortKey, setSortKey] = useState<SortKey>('spend');
  const [sortAsc, setSortAsc] = useState(false);
  const [minSpend, setMinSpend] = useState<number>(5);
  const [showAll, setShowAll] = useState(false);

  const getDateRange = useCallback((): { from: string; to: string } => {
    if (period.type === 'custom') return { from: period.from, to: period.to };
    const days = period.type === 'period' ? parseInt(period.days) : 7;
    const to = new Date();
    to.setUTCDate(to.getUTCDate() - 1);
    const from = new Date(to);
    from.setUTCDate(from.getUTCDate() - days + 1);
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    };
  }, [period]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = getAnalyticsApiBaseUrl();
      const { from, to } = getDateRange();
      const ch = channel === 'all' ? '' : `&channel=${channel}`;
      const res = await fetchWithAuth(
        `${base}/analytics/campaign-roi?from=${from}&to=${to}&minSpend=0&sort=spend${ch}`
      );
      if (!res.ok) {
        setError(`API returned ${res.status}`);
        return;
      }
      const json: ApiResponse = await res.json();
      setData(json);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, channel]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const rows = data.rows.filter((r) => r.spend >= minSpend);
    rows.sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      switch (sortKey) {
        case 'spend': av = a.spend; bv = b.spend; break;
        case 'roi': av = a.roi; bv = b.roi; break;
        case 'paid': av = a.paid; bv = b.paid; break;
        case 'revenue': av = a.revenue; bv = b.revenue; break;
        case 'cpaPaid':
          av = a.cpaPaid > 0 ? a.cpaPaid : Infinity;
          bv = b.cpaPaid > 0 ? b.cpaPaid : Infinity;
          break;
        case 'installs': av = a.installs; bv = b.installs; break;
        case 'campaign':
          return sortAsc
            ? a.campaignName.localeCompare(b.campaignName)
            : b.campaignName.localeCompare(a.campaignName);
      }
      const an = av as number;
      const bn = bv as number;
      return sortAsc ? an - bn : bn - an;
    });
    return rows;
  }, [data, sortKey, sortAsc, minSpend]);

  const totals = data?.totals;

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
        <p className="text-red-300 font-medium">Failed to load campaign ROI: {error}</p>
        <button
          onClick={loadData}
          className="mt-3 px-4 py-1.5 text-sm rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const SortHeader = ({ label, k, w }: { label: string; k: SortKey; w: string }) => (
    <th
      className={`pb-3 font-medium cursor-pointer select-none hover:text-[#FF9C15] ${w} ${sortKey === k ? 'text-[#FF9C15]' : ''}`}
      onClick={() => handleSort(k)}
    >
      {label} {sortKey === k ? (sortAsc ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Header / KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Period</p>
          <p className="text-base font-bold text-white mt-1">{data.from} → {data.to}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Spend</p>
          <p className="text-2xl font-bold text-white mt-1">${formatNumber(totals?.spend || 0)}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Installs</p>
          <p className="text-2xl font-bold text-white mt-1">{formatNumber(totals?.installs || 0)}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Paid Users</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">{formatNumber(totals?.paid || 0)}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Revenue</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">${formatNumber(totals?.revenue || 0)}</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-gray-400 uppercase">Net ROI</p>
          <p className={`text-2xl font-bold mt-1 ${(totals?.roi || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {(totals?.roi || 0) >= 0 ? '+' : ''}{(totals?.roi || 0).toFixed(0)}%
          </p>
        </div>
      </div>

      {/* Filter pills + min-spend control */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {(['all', 'facebook', 'google_ads', 'tiktok'] as Channel[]).map((c) => {
            const active = channel === c;
            return (
              <button
                key={c}
                onClick={() => setChannel(c)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                  active
                    ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white border-transparent'
                    : 'bg-white/5 hover:bg-white/10 border-white/10 text-gray-300'
                }`}
              >
                {c === 'all' ? 'All Networks' : CHANNEL_LABEL[c] || c}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-400 uppercase">Min spend</label>
          <select
            value={minSpend}
            onChange={(e) => setMinSpend(parseInt(e.target.value, 10))}
            className="bg-white/5 border border-white/10 rounded-lg text-sm text-gray-200 px-2 py-1 hover:bg-white/10"
          >
            {[0, 1, 5, 10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>${n}</option>
            ))}
          </select>
          <span className="text-xs text-gray-500">{filtered.length} campaigns</span>
        </div>
      </div>

      {/* Caveats banner */}
      <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-3 text-xs text-yellow-200/80 leading-relaxed">
        <span className="font-semibold text-yellow-200">Caveats:</span> FB CAPI events take 7d-click / 1d-view to fully attribute — fresh FB campaigns may
        under-report. Google revenue is an upper bound (multiple conversion actions track the same purchase). Trials populate as Google ingests
        the new "Trial Start" action. iOS attribution requires an MMP — not in this view.
      </div>

      {/* Campaign table */}
      <ChartCard title={`Campaigns ${channel !== 'all' ? `· ${CHANNEL_LABEL[channel]}` : ''}`}>
        <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0a0b10] z-10">
              <tr className="text-left text-gray-400 border-b border-white/10">
                <th className="pb-3 font-medium w-20">Net</th>
                <th className="pb-3 font-medium w-24">Account</th>
                <SortHeader label="Campaign" k="campaign" w="font-medium" />
                <SortHeader label="Spend" k="spend" w="w-20 text-right" />
                <SortHeader label="Inst" k="installs" w="w-16 text-right" />
                <th className="pb-3 font-medium w-14 text-right">Trial</th>
                <SortHeader label="Paid" k="paid" w="w-14 text-right" />
                <SortHeader label="Revenue" k="revenue" w="w-24 text-right" />
                <SortHeader label="CPA" k="cpaPaid" w="w-20 text-right" />
                <SortHeader label="ROI" k="roi" w="w-20 text-right" />
                <th className="pb-3 font-medium w-20 text-center">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {(showAll ? filtered : filtered.slice(0, 30)).map((r) => {
                const v = getVerdict(r);
                return (
                  <tr
                    key={`${r.channel}-${r.accountId || ''}-${r.campaignId}`}
                    className="border-b border-white/5 hover:bg-white/5"
                  >
                    <td className="py-2.5">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${CHANNEL_COLOR[r.channel] || ''}`}>
                        {CHANNEL_LABEL[r.channel] || r.channel}
                      </span>
                    </td>
                    <td className="py-2.5 text-gray-400 text-xs">{r.accountName || '—'}</td>
                    <td className="py-2.5 font-medium text-white truncate max-w-[280px]" title={r.campaignName}>
                      {r.campaignName}
                    </td>
                    <td className="py-2.5 text-right text-gray-300">${r.spend.toFixed(2)}</td>
                    <td className="py-2.5 text-right text-gray-300">{formatNumber(r.installs)}</td>
                    <td className="py-2.5 text-right text-gray-300">{r.trials || '—'}</td>
                    <td className="py-2.5 text-right text-gray-300">{r.paid || '—'}</td>
                    <td className="py-2.5 text-right text-emerald-400 font-medium">
                      {r.revenue > 0 ? `$${r.revenue.toFixed(2)}` : '—'}
                    </td>
                    <td className="py-2.5 text-right text-gray-300">
                      {r.cpaPaid > 0 ? `$${r.cpaPaid.toFixed(2)}` : '—'}
                    </td>
                    <td className={`py-2.5 text-right font-semibold ${
                      r.roi >= 50 ? 'text-emerald-400'
                      : r.roi >= 0 ? 'text-green-400'
                      : r.roi >= -50 ? 'text-yellow-400'
                      : 'text-red-400'
                    }`}>
                      {r.spend > 0 ? `${r.roi >= 0 ? '+' : ''}${r.roi.toFixed(0)}%` : '—'}
                    </td>
                    <td className={`py-2.5 text-center text-xs ${v.color}`}>{v.text}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-8 text-center text-gray-500 text-sm">
                    No campaigns match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 30 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-6 py-2 text-sm font-semibold rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10 transition-all"
            >
              {showAll ? `Show Top 30 ↑` : `Show All ${filtered.length} Campaigns ↓`}
            </button>
          </div>
        )}
      </ChartCard>

      {/* Channel breakdown */}
      <ChartCard title="Network Roll-up">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(['facebook', 'google_ads', 'tiktok'] as const).map((c) => {
            const rows = filtered.filter((r) => r.channel === c);
            const spend = rows.reduce((s, r) => s + r.spend, 0);
            const revenue = rows.reduce((s, r) => s + r.revenue, 0);
            const paid = rows.reduce((s, r) => s + r.paid, 0);
            const installs = rows.reduce((s, r) => s + r.installs, 0);
            const roi = spend > 0 ? ((revenue - spend) / spend) * 100 : 0;
            return (
              <div key={c} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${CHANNEL_COLOR[c]}`}>
                    {CHANNEL_LABEL[c]}
                  </span>
                  <span className="text-xs text-gray-500">{rows.length} campaigns</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Spend</p>
                    <p className="text-lg font-bold text-white">${formatNumber(spend)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Revenue</p>
                    <p className="text-lg font-bold text-emerald-400">${formatNumber(revenue)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Installs</p>
                    <p className="text-base font-semibold text-gray-300">{formatNumber(installs)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Paid</p>
                    <p className="text-base font-semibold text-gray-300">{paid}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 uppercase">ROI</p>
                    <p className={`text-xl font-bold ${roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {roi >= 0 ? '+' : ''}{roi.toFixed(0)}%
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ChartCard>
    </div>
  );
};

export default AdsRoiTab;
